import clsx from "clsx";

import { Button } from "../Button";
import { AddToAListIconPNG, ErrorIconV2, resolveImageSrc } from "../Icons";
import { Typo } from "../Typo";

export type TableStateVariant = "error" | "empty";

type TableStateProps = {
  /**
   * `error` shows the red-cross illustration; `empty` shows the document + lens
   * illustration. Either renders the action button when `actionLabel` and
   * `onAction` are both given. The illustration is fixed per variant — it is not overridable, so the two
   * states stay visually distinguishable wherever a table renders them.
   */
  variant: TableStateVariant;
  title: string;
  description: string;
  /** Action button label; the button renders only together with `onAction`. */
  actionLabel?: string;
  /** Action button handler; the button renders only together with `actionLabel`. */
  onAction?: () => void;
  /**
   * Sizing/spacing for the region the state fills — the host table decides how
   * tall its body is, so height is deliberately not baked in here.
   */
  className?: string;
};

/**
 * Centred block that fills a table's body region while the table header stays
 * in place — the shared element behind both the "no rows" and "failed to load"
 * states (`TableDesktop`/`TableMobile`'s `emptyComponent` / `errorComponent`
 * slots).
 *
 * Spacing follows the design canvas (`js/TableState.jsx`): illustration → title
 * 24px, title → description 8px, description → button 24px. The outer `gap-6`
 * supplies both 24px gaps because the title and description live together in
 * one `gap-2` child, making them a single flex item.
 */
export const TableState = ({
  variant,
  title,
  description,
  actionLabel,
  onAction,
  className,
}: TableStateProps) => {
  const showAction = Boolean(actionLabel) && Boolean(onAction);

  return (
    <div
      // `alert` announces a failure the user did not ask for; an empty result
      // is an expected outcome of their own filtering, so it is only `status`.
      role={variant === "error" ? "alert" : "status"}
      data-testid={`table-state-${variant}`}
      className={clsx(
        "mx-auto flex w-full flex-col items-center justify-center gap-6 p-6 text-center",
        className,
      )}
    >
      {variant === "error" ? (
        <ErrorIconV2
          className="h-16 w-16 shrink-0"
          aria-hidden="true"
          focusable="false"
        />
      ) : (
        <img
          src={resolveImageSrc(AddToAListIconPNG)}
          alt=""
          aria-hidden="true"
          className="size-16 shrink-0"
        />
      )}
      <div className="flex max-w-[360px] flex-col items-center gap-2">
        <Typo element="span" type="subheadingMD" textColor="text-text-primary">
          {title}
        </Typo>
        <Typo element="span" type="bodySM" textColor="text-text-secondary">
          {description}
        </Typo>
      </div>
      {showAction && (
        <Button
          variant="brand"
          size="md"
          onClick={onAction}
          data-testid="table_state_action_button"
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
};
